interface RGB {
  r: number;
  g: number;
  b: number;
}

export function HSLToRGB(h: number, s: number, l: number): RGB {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

export function RGBToHex(r: number, g: number, b: number): string {
  const toHex = (n: number): string => {
    const hex = n.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return "#" + toHex(r) + toHex(g) + toHex(b);
}

const colorNameToHex: { [key: string]: string } = {
  black: "#000000",
  silver: "#C0C0C0",
  gray: "#808080",
  white: "#FFFFFF",
  maroon: "#800000",
  red: "#FF0000",
  purple: "#800080",
  fuchsia: "#FF00FF",
  green: "#008000",
  lime: "#00FF00",
  olive: "#808000",
  yellow: "#FFFF00",
  navy: "#000080",
  blue: "#0000FF",
  teal: "#008080",
  aqua: "#00FFFF",
  // Tailwind CSS default colors for convenience
  "slate-500": "#64748b",
  "gray-500": "#6b7280",
  "zinc-500": "#71717a",
  "neutral-500": "#737373",
  "stone-500": "#78716c",
  "red-500": "#ef4444",
  "orange-500": "#f97316",
  "amber-500": "#f59e0b",
  "yellow-500": "#eab308",
  "lime-500": "#84cc16",
  "green-500": "#22c55e",
  "emerald-500": "#10b981",
  "teal-500": "#14b8a6",
  "cyan-500": "#06b6d4",
  "sky-500": "#0ea5e9",
  "blue-500": "#3b82f6",
  "indigo-500": "#6366f1",
  "violet-500": "#8b5cf6",
  "purple-500": "#a855f7",
  "fuchsia-500": "#d946ef",
  "pink-500": "#ec4899",
  "rose-500": "#f43f5e",
};

function isHexColor(color: string): boolean {
  if (!color || typeof color !== "string") return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color.trim());
}

export function resolveColor(color: string | undefined): string | null {
  if (!color) return null;
  if (isHexColor(color)) {
    return color;
  }
  return colorNameToHex[color.toLowerCase()] || null;
}

// Gradient utility functions abstracted from Button component
export function hexToRgba(hex: string, alpha = 0.18): string {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export interface GradientOptions {
  colors: string[];
  direction?: "horizontal" | "vertical" | "diagonal";
  alpha?: number;
}

export function createLinearGradient({
  colors,
  direction = "horizontal",
  alpha = 0.18,
}: GradientOptions): string {
  const colorStops = colors
    .map(
      (c, i) =>
        `${hexToRgba(c, alpha)} ${Math.round(
          (i / (colors.length - 1 || 1)) * 100
        )}%`
    )
    .join(", ");

  switch (direction) {
    case "vertical":
      return `linear-gradient(to bottom, ${colorStops})`;
    case "diagonal":
      return `linear-gradient(135deg, ${colorStops})`;
    default:
      return `linear-gradient(to right, ${colorStops})`;
  }
}

export interface ConicBorderOptions {
  colors: string[];
  opacity?: number;
  startAngle?: number;
}

export function createConicBorderStyle({
  colors,
  opacity = 0.6,
  startAngle = 206,
}: ConicBorderOptions): React.CSSProperties {
  if (!colors || colors.length === 0) {
    return {};
  }

  // Create color stops for conic gradient
  const stops = colors.map((c, i) => {
    const deg = Math.round((i / colors.length) * 360);
    return `${c} ${deg}deg`;
  });
  // Close the loop
  stops.push(`${colors[0]} 360deg`);

  return {
    position: "absolute" as const,
    inset: 0,
    zIndex: 0,
    mixBlendMode: "screen" as const,
    padding: "1px",
    pointerEvents: "none" as const,
    opacity,
    background: `conic-gradient(from var(--border-angle, 0turn), ${stops.join(", ")})`,
    mask: "linear-gradient(#fff 0 0) content-box exclude, linear-gradient(#fff 0 0)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box exclude, linear-gradient(#fff 0 0)",
  };
}

export interface GradientBackgroundOptions {
  colors: string[];
  direction?: "horizontal" | "vertical" | "diagonal";
  alpha?: number;
}

export function createGradientBackground({
  colors,
  direction = "horizontal",
  alpha = 0.18,
}: GradientBackgroundOptions): React.CSSProperties {
  return {
    background: createLinearGradient({ colors, direction, alpha }),
  };
}

// Helper to generate dark mode colors that match light mode opacity colors
export function getDarkModeColor(hex: string): string {
  // Convert hex to RGB
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Adjust the color to be more vibrant in dark mode
  // Keep saturation moderate but increase brightness
  const hsl = rgbToHsl(r, g, b);
  // Hue is kept the same
  hsl[1] = Math.min(100, hsl[1] * 1.2); // Slightly increase saturation
  hsl[2] = Math.min(100, hsl[2] * 1.35); // Increase brightness moderately

  // Add minimal white to enhance vibrancy without washing out
  const rgb = hslToRgb(hsl[0], hsl[1], hsl[2]);
  const whiteAmount = 0.1; // Reduced white amount to 10%

  return `#${Math.round(rgb[0] + (255 - rgb[0]) * whiteAmount)
    .toString(16)
    .padStart(2, "0")}${Math.round(rgb[1] + (255 - rgb[1]) * whiteAmount)
    .toString(16)
    .padStart(2, "0")}${Math.round(rgb[2] + (255 - rgb[2]) * whiteAmount)
    .toString(16)
    .padStart(2, "0")}`;
}

// Helper to calculate relative luminance
export function luminance(hex: string): number {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Helper to calculate contrast ratio
export function contrastRatio(hex1: string, hex2: string): number {
  const l1 = luminance(hex1);
  const l2 = luminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

// Helper to blend two hex colors by alpha
export function blendHexColors(fgHex: string, bgHex: string, alpha: number): string {
  fgHex = fgHex.replace("#", "");
  bgHex = bgHex.replace("#", "");
  const fr = parseInt(fgHex.substring(0, 2), 16);
  const fg = parseInt(fgHex.substring(2, 4), 16);
  const fb = parseInt(fgHex.substring(4, 6), 16);
  const br = parseInt(bgHex.substring(0, 2), 16);
  const bg = parseInt(bgHex.substring(2, 4), 16);
  const bb = parseInt(bgHex.substring(4, 6), 16);
  const r = Math.round(fr * alpha + br * (1 - alpha));
  const g = Math.round(fg * alpha + bg * (1 - alpha));
  const b = Math.round(fb * alpha + bb * (1 - alpha));
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

// Helper to determine best text color for contrast (WCAG AA compliant)
export function getContrastTextColor(
  hex: string,
  opacity: number = 1,
  isDarkMode: boolean = false
): string {
  // Parent background
  const parentBg = isDarkMode ? "#181f2a" : "#ffffff";
  // Blend with parent background if opacity < 1
  let effectiveBg = hex;
  if (opacity < 1) {
    effectiveBg = blendHexColors(hex, parentBg, opacity);
  }
  // Test both white and dark text
  const white = "#ffffff";
  const dark = "#18181b"; // tailwind slate-900
  const whiteContrast = contrastRatio(effectiveBg, white);
  const darkContrast = contrastRatio(effectiveBg, dark);
  // WCAG AA requires at least 4.5:1 for normal text
  if (whiteContrast >= 4.5 && whiteContrast >= darkContrast) {
    return "text-white";
  } else if (darkContrast >= 4.5) {
    return "text-slate-900";
  } else {
    // fallback: pick the higher contrast
    return whiteContrast > darkContrast ? "text-white" : "text-slate-900";
  }
}

// Helper functions for color conversion
export function rgbToHsl(
  r: number,
  g: number,
  b: number
): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

export function hslToRgb(
  h: number,
  s: number,
  l: number
): [number, number, number] {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

// Helper to darken a hex color
export function darkenHexColor(hex: string, amount: number = 0.2): string {
  hex = hex.replace("#", "");
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  r = Math.max(0, Math.min(255, Math.floor(r * (1 - amount))));
  g = Math.max(0, Math.min(255, Math.floor(g * (1 - amount))));
  b = Math.max(0, Math.min(255, Math.floor(b * (1 - amount))));
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Function to generate a secondary color for the gradient
export function getSecondaryColor(hex: string): string {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const hsl = rgbToHsl(r, g, b);
  hsl[0] = (hsl[0] + 45) % 360; // Shift hue by 45 degrees
  hsl[1] = Math.min(100, hsl[1] * 1.1); // Slightly boost saturation
  hsl[2] = Math.max(0, hsl[2] * 0.95); // Slightly darken
  const [r2, g2, b2] = hslToRgb(hsl[0], hsl[1], hsl[2]);
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
}

// Color palettes inspired by watercolor (shared between WorkerAvatar and GradientBlobContainer)
// These color arrays ensure consistent color selection across components
export const mainColors = [
  "#3D5AFE", // Vibrant Blue
  "#F50057", // Vibrant Pink
  "#00E676", // Vibrant Green
  "#FFD600", // Vibrant Yellow
  "#651FFF", // Vibrant Purple
  "#1DE9B6", // Vibrant Teal
  "#FF9100", // Vibrant Orange
  "#FF1744", // Vibrant Red
  "#00BCD4", // Vibrant Cyan
  "#9C27B0", // Vibrant Magenta
  "#4CAF50", // Vibrant Lime Green
  "#FF9800", // Vibrant Amber
  "#E91E63", // Vibrant Rose
  "#2196F3", // Vibrant Light Blue
  "#8BC34A", // Vibrant Light Green
  "#FF5722", // Vibrant Deep Orange
  "#9E9E9E", // Vibrant Grey
  "#607D8B", // Vibrant Blue Grey
];

export const overlay1Colors = [
  "#8C9EFF", // Lighter Blue
  "#FF80AB", // Lighter Pink
  "#69F0AE", // Lighter Green
  "#FFEA00", // Lighter Yellow
  "#7C4DFF", // Lighter Purple
  "#64FFDA", // Lighter Teal
  "#FFAB40", // Lighter Orange
  "#FF8A80", // Lighter Red
  "#84FFFF", // Lighter Cyan
  "#E040FB", // Lighter Magenta
  "#8BC34A", // Lighter Lime Green
  "#FFD54F", // Lighter Amber
  "#F48FB1", // Lighter Rose
  "#81D4FA", // Lighter Light Blue
  "#AED581", // Lighter Light Green
  "#FFAB91", // Lighter Deep Orange
  "#BDBDBD", // Lighter Grey
  "#90A4AE", // Lighter Blue Grey
];

// Deterministic hash function for string seed (shared between components)
// This ensures the same seed always produces the same hash value
export function hashString(str: string | null | undefined): number {
  if (!str) return 0;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
}

type ColorTheory = "complementary" | "split-complementary" | "triadic" | "analogous";

export const DEFAULT_COLOR_THEORY: ColorTheory = "triadic";

export function generateColorsFromSeed(
  seed: string | null | undefined
): {
  color1: string;
  color2: string;
  hotspotColor?: string;
} {
  const hash = hashString(seed || "default");
  const hue1 = hash % 360;
  let hue2: number;

  switch (DEFAULT_COLOR_THEORY) {
    case "complementary":
      hue2 = (hue1 + 180) % 360;
      break;
    case "triadic":
      hue2 = (hue1 + 120) % 360;
      break;
    case "analogous":
      hue2 = (hue1 + 30) % 360;
      break;
    case "split-complementary":
    default: {
      // Randomly choose split offset for more variety and harmony
      const splitOffsets = [150, 210];
      const splitOffset = splitOffsets[hash % 2];
      hue2 = (hue1 + splitOffset) % 360;
      break;
    }
  }

  const color1 = `hsl(${hue1}, 95%, 60%)`;
  const color2 = `hsl(${hue2}, 95%, 60%)`;
  return {
    color1,
    color2,
    hotspotColor: color2,
  };
}

// Test function to verify color consistency (for development/debugging)
export function testColorConsistency(seed: string): void {
  const colors = generateColorsFromSeed(seed);
  console.log(`Colors for seed "${seed}":`, colors);
  console.log(`Hash value: ${hashString(seed)}`);
}
